import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useAuthStore } from "@/store/authStore";
import { Permission, UserRole } from "@/types";
import { legalInfoQueryKey, profileService } from "@/services/profileService";
import { PageFrame, SectionCard, QuickActionGrid } from "@/components/common";
import { PartnerNeedsAttentionPanel } from "@/components/bookings";
import { OptionCard, type OptionCardLinkRenderProps } from "@/components/ui";
import {
  DashboardStats,
  RecentTransactions,
  AdminDashboardStats,
  AdminDashboardNotifications,
  SalesAgentDashboardStats,
} from "./components";
import {
  useDashboardData,
  useAdminDashboardData,
  useSalesAgentDashboardData,
} from "./hooks";
import {
  dashboardKindFor,
  profileDisplayName,
  timeGreetingKey,
} from "./dashboardRules";
import {
  Building2,
  Calendar,
  Wallet,
  Award,
  Link2,
  Home,
  LifeBuoy,
  Zap,
} from "lucide-react";
import { bookingService } from "@/services/bookingService";

function renderOptionLink({
  href,
  className,
  children,
}: OptionCardLinkRenderProps) {
  return (
    <Link
      to={href}
      className={className}
    >
      {children}
    </Link>
  );
}

/** Main column (feeds, lists) + narrow quick-actions rail on wide screens. */
function DashboardSplit({
  main,
  aside,
}: {
  main: ReactNode;
  aside: ReactNode;
}) {
  return (
    <div className="grid items-start gap-5 xl:grid-cols-3">
      <div className="min-w-0 space-y-5 xl:col-span-2">{main}</div>
      <div className="min-w-0 xl:sticky xl:top-20">{aside}</div>
    </div>
  );
}

function QuickActionsCard({ children }: { children: ReactNode }) {
  const { t } = useTranslation("dashboard");
  return (
    <SectionCard
      title={t("shared.quickActions")}
      icon={Zap}
    >
      <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-1">
        {children}
      </div>
    </SectionCard>
  );
}

function useTimeGreeting() {
  const { t } = useTranslation("common");
  return t(timeGreetingKey(new Date().getHours()));
}

function useProfileGreetingTitle(greetingWithNameKey: string): string {
  const { t } = useTranslation("dashboard");
  const greet = useTimeGreeting();
  const user = useAuthStore((s) => s.user);
  const userId = user?.sub;
  const { data: profile, isSuccess } = useQuery({
    queryKey: legalInfoQueryKey(userId ?? ""),
    queryFn: () => profileService.getProfile(userId!),
    enabled:
      !!userId &&
      user.role !== UserRole.ADMIN &&
      user.role !== UserRole.CUSTOMER,
  });
  const displayName = isSuccess ? profileDisplayName(profile) : null;

  if (displayName) {
    return t(greetingWithNameKey, { greeting: greet, name: displayName });
  }
  return greet;
}

// Admin Dashboard Component
function AdminDashboard() {
  const { t } = useTranslation("dashboard");
  const { stats, canFetch, isLoading, error } = useAdminDashboardData();
  const greet = useTimeGreeting();
  const headerTitle = t("admin.greetingWithName", { greeting: greet });
  const headerDescription = t("admin.overviewStats");

  return (
    <PageFrame
      title={headerTitle}
      description={headerDescription}
      isLoading={isLoading}
      loadingMessage={t("shared.loadingData")}
      error={canFetch && !!error}
      errorMessage={t("shared.errorLoad")}
      onRetry={() => window.location.reload()}
    >
      {stats ? (
        <AdminDashboardStats stats={stats} />
      ) : null}

      <AdminDashboardNotifications />
    </PageFrame>
  );
}

// Partner Dashboard Component
function PartnerDashboard() {
  const { t } = useTranslation("dashboard");
  const { getCurrentUserId } = useAuthStore();
  const sellerUserId = getCurrentUserId();

  const { walletBalance, stats, isLoading, error } =
    useDashboardData(sellerUserId);

  const greetingTitle = useProfileGreetingTitle("shared.greetingWithName");
  const hasSeller = Boolean(sellerUserId);

  return (
    <PageFrame
      title={hasSeller ? greetingTitle : t("shared.title")}
      description={hasSeller ? t("partner.greeting") : t("partner.overview")}
      error={!hasSeller || !!error}
      errorMessage={
        !hasSeller ? t("partner.sellerIdMissing") : t("shared.errorLoad")
      }
      onRetry={() => window.location.reload()}
      isLoading={hasSeller && isLoading}
      loadingMessage={t("shared.loadingData")}
    >
      <DashboardStats
        walletBalance={walletBalance}
        currentBookings={stats.currentBookings}
        bookingChange={stats.bookingChange}
        currentRevenue={stats.currentRevenue}
        revenueChange={stats.revenueChange}
        transactionsCount={stats.transactionsCount}
      />

      <DashboardSplit
        main={
          sellerUserId ? (
            <PartnerNeedsAttentionPanel />
          ) : null
        }
        aside={
          <QuickActionsCard>
            <OptionCard
              to="/activities"
              icon={Building2}
              tone="primary"
              title={t("partner.myActivities")}
              description={t("partner.myActivitiesDesc")}
              renderLink={renderOptionLink}
            />
            <OptionCard
              to="/bookings"
              icon={Calendar}
              tone="accent"
              title={t("partner.bookings")}
              description={t("partner.bookingsDesc")}
              renderLink={renderOptionLink}
            />
            <OptionCard
              to="/wallet"
              icon={Wallet}
              tone="success"
              title={t("shared.wallet")}
              description={t("partner.walletDesc")}
              renderLink={renderOptionLink}
            />
          </QuickActionsCard>
        }
      />
    </PageFrame>
  );
}

// Host Dashboard Component
function HostDashboard() {
  const { t } = useTranslation("dashboard");
  const { getCurrentUserId, hasPermission } = useAuthStore();
  const hostId = getCurrentUserId();

  const { walletBalance, stats, recentTransactions, isLoading, error } =
    useDashboardData(hostId, { includeRecentTransactions: true });

  const greetingTitle = useProfileGreetingTitle("shared.greetingWithName");
  const hasHost = Boolean(hostId);

  return (
    <PageFrame
      title={hasHost ? greetingTitle : t("shared.title")}
      description={hasHost ? t("host.greeting") : t("host.overview")}
      error={!hasHost || !!error}
      errorMessage={!hasHost ? t("host.hostIdMissing") : t("shared.errorLoad")}
      onRetry={() => window.location.reload()}
      isLoading={hasHost && isLoading}
      loadingMessage={t("shared.loadingData")}
    >
      <DashboardStats
        walletBalance={walletBalance}
        currentBookings={stats.currentBookings}
        bookingChange={stats.bookingChange}
        currentRevenue={stats.currentRevenue}
        revenueChange={stats.revenueChange}
        transactionsCount={stats.transactionsCount}
      />

      <DashboardSplit
        main={<RecentTransactions transactions={recentTransactions} />}
        aside={
          <QuickActionsCard>
            {hasPermission(Permission.HOUSES_READ) ? (
              <OptionCard
                to="/houses"
                icon={Home}
                tone="primary"
                title={t("host.myStructures")}
                description={t("host.myStructuresDesc")}
                renderLink={renderOptionLink}
              />
            ) : null}
            {hasPermission(Permission.WALLET_READ) ? (
              <OptionCard
                to="/wallet"
                icon={Wallet}
                tone="success"
                title={t("shared.wallet")}
                description={t("host.walletDesc")}
                renderLink={renderOptionLink}
              />
            ) : null}
            {hasPermission(Permission.SUPPORT_TICKETS_CREATE) ? (
              <OptionCard
                to="/support"
                icon={LifeBuoy}
                tone="accent"
                title={t("salesAgent.support")}
                description={t("salesAgent.supportDesc")}
                renderLink={renderOptionLink}
              />
            ) : null}
          </QuickActionsCard>
        }
      />
    </PageFrame>
  );
}

function SalesAgentDashboard() {
  const { t } = useTranslation("dashboard");
  const { getCurrentUserId } = useAuthStore();
  const userId = getCurrentUserId();

  const { dashboard, recentTransactions, isLoading, error } =
    useSalesAgentDashboardData(userId);

  const greetingTitle = useProfileGreetingTitle("shared.greetingWithName");
  const hasUser = Boolean(userId);

  return (
    <PageFrame
      title={hasUser ? greetingTitle : t("shared.title")}
      description={
        hasUser ? t("salesAgent.greeting") : t("salesAgent.overview")
      }
      error={!hasUser || !!error || !dashboard}
      errorMessage={
        !hasUser ? t("salesAgent.userIdMissing") : t("salesAgent.errorLoad")
      }
      onRetry={() => window.location.reload()}
      isLoading={hasUser && isLoading}
      loadingMessage={t("salesAgent.loading")}
    >
      {dashboard ? <SalesAgentDashboardStats data={dashboard} /> : null}

      <DashboardSplit
        main={<RecentTransactions transactions={recentTransactions} />}
        aside={
          <QuickActionsCard>
            <OptionCard
              to="/wallet"
              icon={Wallet}
              tone="success"
              title={t("shared.wallet")}
              description={t("salesAgent.walletDesc")}
              renderLink={renderOptionLink}
            />
            <OptionCard
              to="/associations"
              icon={Link2}
              tone="primary"
              title={t("salesAgent.assignments")}
              description={t("salesAgent.assignmentsDesc")}
              renderLink={renderOptionLink}
            />
            <OptionCard
              to="/support"
              icon={LifeBuoy}
              tone="accent"
              title={t("salesAgent.support")}
              description={t("salesAgent.supportDesc")}
              renderLink={renderOptionLink}
            />
          </QuickActionsCard>
        }
      />
    </PageFrame>
  );
}

// Main Dashboard Page
export default function DashboardPage() {
  const { user, hasPermission } = useAuthStore();

  switch (dashboardKindFor(user?.role, hasPermission)) {
    case "customer":
      return <CustomerDashboard />;
    case "admin":
      return <AdminDashboard />;
    case "salesAgent":
      return <SalesAgentDashboard />;
    case "host":
      return <HostDashboard />;
    case "partner":
      return <PartnerDashboard />;
  }
}

function CustomerDashboard() {
  const { t } = useTranslation("dashboard");
  const { data: points } = useQuery({
    queryKey: ["customer-points-summary"],
    queryFn: () => bookingService.getCustomerPointsSummary(),
  });

  return (
    <PageFrame
      title={t("customer.welcomeTitle")}
      description={t("customer.welcomeDesc")}
    >
      <QuickActionGrid columns={2}>
        <OptionCard
          to="/bookings"
          icon={Calendar}
          tone="primary"
          surface="elevated"
          title={t("customer.myBookings")}
          description={t("customer.myBookingsDesc")}
          renderLink={renderOptionLink}
        />
        <OptionCard
          to="/points"
          icon={Award}
          tone="success"
          surface="elevated"
          title={t("customer.pointsBalance")}
          description={t("customer.pointsCount", {
            count: points?.points_balance ?? 0,
          })}
          renderLink={renderOptionLink}
        />
      </QuickActionGrid>
    </PageFrame>
  );
}
